import { useEffect, useMemo, useState } from 'react';

import { Box, FormControl, FormLabel, HStack, Select, Skeleton, useColorModeValue } from '@ecoinvent/ui';

import useV4Indicators from 'hooks/data/datasets/V4/lcia/useV4Indicators';

import { COLORS } from './constants';

// `category` is the LCIA category level shown in the UI; `indicatorId` is the UUID the score API consumes.
export type LciaSelection = {
  method: string;
  category: string;
  indicator: string;
  indicatorId: string;
};

// Confirmed landing selection. /web/v4/indicators is release-scoped, so each is a preference, not a guarantee.
const DEFAULT_METHOD = 'IPCC 2021';
const DEFAULT_CATEGORY = 'climate change: total (excl. biogenic CO2)';
const DEFAULT_INDICATOR = 'global warming potential (GWP100)';

// Current pick, else the confirmed default, else the branch's first option — never empty while options exist.
const resolveLevel = (options: string[], picked: string, preferred: string) =>
  options.includes(picked) ? picked : options.includes(preferred) ? preferred : (options[0] ?? '');

type Props = {
  initialSelection: LciaSelection | null;
  onChange: (selection: LciaSelection) => void;
};

const SupplyChainLciaFilters = ({ initialSelection, onChange }: Props) => {
  const bg = useColorModeValue('white', COLORS.darkSurface);
  const borderColor = useColorModeValue(COLORS.borderLight, COLORS.buttonOutlinedBorderDark);
  const boxShadow = useColorModeValue('sm', 'dark-lg');

  const { data, isLoading } = useV4Indicators();
  // Seeded so a remount (fullscreen, mode switch) resumes the live selection instead of snapping back to defaults.
  const [picked, setPicked] = useState({
    method: initialSelection?.method ?? '',
    category: initialSelection?.category ?? '',
    indicator: initialSelection?.indicator ?? '',
  });

  const methods = useMemo(() => Object.keys(data ?? {}), [data]);
  // Every level is re-resolved against the current branch, so a stale pick can never form an invalid combination.
  const method = resolveLevel(methods, picked.method, DEFAULT_METHOD);

  const categories = useMemo(() => Object.keys(data?.[method] ?? {}), [data, method]);
  const category = resolveLevel(categories, picked.category, DEFAULT_CATEGORY);

  const indicatorIds = useMemo(() => data?.[method]?.[category] ?? {}, [data, method, category]);
  const indicators = useMemo(() => Object.keys(indicatorIds), [indicatorIds]);
  const indicator = resolveLevel(indicators, picked.indicator, DEFAULT_INDICATOR);
  const indicatorId = indicatorIds[indicator] ?? '';

  useEffect(() => {
    onChange({ method, category, indicator, indicatorId });
  }, [onChange, method, category, indicator, indicatorId]);

  return (
    // Paints above the graph: the `.react-flow` wrapper is its own `z-index: 0` stacking context.
    <Box
      position="absolute"
      top={4}
      // Spans the canvas: anchored to both insets rather than a fixed width, so it tracks the container.
      left={4}
      right={4}
      zIndex={10}
      bg={bg}
      borderRadius="lg"
      border={`1px solid ${borderColor}`}
      boxShadow={boxShadow}
      p={3}
    >
      <Skeleton isLoaded={!isLoading} borderRadius="md">
        {/* The three controls share the panel width equally; minW={0} lets a long value ellipsize instead of overflowing. */}
        <HStack spacing={3} align="flex-start">
          <FormControl size="sm" flex={1} minW={0}>
            <FormLabel fontSize="xs" mb={1} fontWeight="semibold">
              LCIA Method
            </FormLabel>
            <Select
              size="sm"
              variant="outline"
              borderRadius="md"
              value={method}
              onChange={(e) => setPicked({ method: e.target.value, category: '', indicator: '' })}
              isDisabled={!methods.length}
            >
              {methods.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl size="sm" flex={1} minW={0}>
            <FormLabel fontSize="xs" mb={1} fontWeight="semibold">
              Category
            </FormLabel>
            <Select
              size="sm"
              variant="outline"
              borderRadius="md"
              value={category}
              onChange={(e) => setPicked((prev) => ({ ...prev, category: e.target.value, indicator: '' }))}
              isDisabled={!categories.length}
            >
              {categories.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl size="sm" flex={1} minW={0}>
            <FormLabel fontSize="xs" mb={1} fontWeight="semibold">
              Indicator
            </FormLabel>
            <Select
              size="sm"
              variant="outline"
              borderRadius="md"
              value={indicator}
              onChange={(e) => setPicked((prev) => ({ ...prev, indicator: e.target.value }))}
              isDisabled={!indicators.length}
            >
              {indicators.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
          </FormControl>
        </HStack>
      </Skeleton>
    </Box>
  );
};

export default SupplyChainLciaFilters;
